import { gql } from "@apollo/client";
import { CurrencyCode } from "@asmbl/shared/constants";
import { add, Money, subtract, zero } from "@asmbl/shared/money";
import { mapMaybe } from "@asmbl/shared/utils";
import { TableCell, TableRow, Typography } from "@mui/material";
import makeStyles from "../../../../makeStylesV4Compat";
import clsx from "clsx";
import { Fragment } from "react";
import {
  SalarySummarizedRow_compCycleBudget as CompCycleBudget,
  SalarySummarizedRow_compCycleData as CompCycleData,
  SalarySummarizedRow_manager as Manager,
  SalarySummarizedRow_reports as Report,
} from "../../../../__generated__/graphql";
import { CompCycleDisplay } from "../../../../components/CompCycle/types";
import { useCurrencies } from "../../../../components/CurrenciesContext";
import { formatBudget } from "../../../../models/Budget";
import {
  formatRequest,
  formatVariance,
} from "../../../../models/CompRecommendation";
import { DV_RED, GRAY_4, GRAY_8 } from "../../../../theme";
import { IndentationCells } from "../../Budgets/Table/Connectors";
import {
  calculateRequestsFromReportsForSalary,
  getSalaryCompComponents,
} from "./SalaryRow";
import { SummarizedNameCell } from "./SummarizedNameCell";

type Props = {
  rootBudget: CompCycleBudget | null;
  manager: Manager | null;
  reports: Report[];
  organizationName: string;
  varianceDisplay: CompCycleDisplay;
  compCycleData: CompCycleData;
  indentation: IndentationCells;
};

type BudgetKey = "salary" | "salaryMarket" | "salaryMerit" | "salaryPromotion";

const useStyles = makeStyles(() => ({
  root: {
    height: "1.5rem",

    "& tr": {
      height: "1.5rem",
    },

    "& td": {
      height: "24px",
      paddingTop: 0,
      paddingBottom: 0,
      backgroundColor: GRAY_8,
    },
  },
  text: {
    color: GRAY_4,
    fontSize: "0.8125rem",
    lineHeight: "1.259375",
    fontWeight: 400,
    letterSpacing: "-0.25px",
  },
  alertText: { color: DV_RED },
}));

function getTopLevelBudget(
  rootBudget: CompCycleBudget | null,
  manager: Manager | null,
  budgetKey: BudgetKey
): Money | null {
  // if this budget is an organization level budget
  if (manager === null && rootBudget && rootBudget[budgetKey]) {
    return rootBudget[budgetKey];
  }

  // if this budget is attached to an employee
  if (manager !== null && manager.compCycleBudget?.[budgetKey]) {
    return manager.compCycleBudget[budgetKey];
  }

  return null;
}

function getReportsBudget(
  defaultCurrencyCode: CurrencyCode,
  reports: Report[],
  budgetKey: BudgetKey
): Money | null {
  const mappedBudget = mapMaybe(
    reports,
    (employee) => employee.compCycleBudget?.[budgetKey]
  );

  if (mappedBudget.length === 0) return null;

  return mappedBudget.reduce(add, zero(defaultCurrencyCode));
}

export function SalarySummarizedRow({
  rootBudget,
  manager,
  reports,
  organizationName,
  varianceDisplay,
  compCycleData,
  indentation,
}: Props): JSX.Element {
  const classes = useStyles();
  const compComponents = getSalaryCompComponents(compCycleData);
  const { defaultCurrencyCode } = useCurrencies();

  return (
    <TableRow className={classes.root}>
      {/* Name & title cell + direct & indirect cells */}
      <SummarizedNameCell
        subjectName={manager ? manager.displayName : organizationName}
        indentation={indentation}
      />

      {/* Direct & indirect cells */}
      <TableCell colSpan={1} />
      <TableCell colSpan={1} />

      {compComponents
        .map(({ budgetKey, requestType }) => ({
          budgetKey,
          budgetData: {
            top: getTopLevelBudget(rootBudget, manager, budgetKey),
            current: getReportsBudget(defaultCurrencyCode, reports, budgetKey),
          },
          requests: calculateRequestsFromReportsForSalary(reports, requestType),
        }))
        .map(({ budgetKey, budgetData, requests }) => {
          const budget = budgetData.top
            ? subtract(
                budgetData.top,
                budgetData.current ?? zero(defaultCurrencyCode)
              )
            : null;

          const variance =
            budget && requests ? subtract(requests, budget) : null;

          return (
            <Fragment key={budgetKey}>
              {/* budget */}
              <TableCell align="right">
                <Typography className={classes.text}>
                  {formatBudget(budget)}
                </Typography>
              </TableCell>

              {/* request */}
              <TableCell align="right">
                <Typography className={classes.text}>
                  {formatRequest(requests)}
                </Typography>
              </TableCell>

              {/* variance */}
              <TableCell align="right">
                <Typography
                  className={clsx(classes.text, {
                    [classes.alertText]: variance && variance.value > 0,
                  })}
                >
                  {formatVariance(varianceDisplay, budget, variance)}
                </Typography>
              </TableCell>
            </Fragment>
          );
        })}
    </TableRow>
  );
}

SalarySummarizedRow.fragments = {
  compCycleBudget: gql`
    fragment SalarySummarizedRow_compCycleBudget on CompCycleBudget {
      compCycleId
      employeeId
      salary
      salaryMarket
      salaryMerit
      salaryPromotion
    }
  `,
  manager: gql`
    fragment SalarySummarizedRow_manager on Employee {
      id
      displayName

      compCycleBudget(compCycleId: $compCycleId) {
        compCycleId
        employeeId

        salary
        salaryMarket
        salaryMerit
        salaryPromotion
      }
    }
  `,
  reports: gql`
    fragment SalarySummarizedRow_reports on Employee {
      id
      displayName

      compRecommendation(compCycleId: $compCycleId) {
        subjectId
        compCycleId
        reviewStatus
        latestSubmittedItems {
          id
          submittedAt
          recommendationType
          annualCashEquivalent(currencyCode: $currencyCode)
        }
      }

      compCycleBudget(compCycleId: $compCycleId) {
        compCycleId
        employeeId
        salary
        salaryMarket
        salaryMerit
        salaryPromotion
      }
    }
  `,
  compCycleData: gql`
    fragment SalarySummarizedRow_compCycleData on CompCycle {
      id
      allowSalary
      allowSalaryMarket
      allowSalaryMerit
      allowSalaryPromotion
    }
  `,
};
